import { useState, useEffect, useMemo, useCallback, useRef, Suspense } from 'react';
import { lazyWithRetries } from 'src/routes/lazyRetry';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
    Box, Typography, IconButton, Button, Tooltip, Dialog,
    TextField, Stack, MenuItem, Select, CircularProgress,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Icon } from '@iconify/react';
import moment from 'moment';
import GoogleAutocomplete from 'src/components/AddressAutocomplete';
import DateOfBirthField from 'src/components/fields/DateOfBirthField';
import { callApi } from '../../utils/apiCall';
import { useAuth } from '../../hooks';
import { isWithin24Hours, getLastInboundMessageCreatedAt } from './whatsappUtil';
import OverviewTab from '../../sections/@dashboard/lead/OverviewTab';
import { getPracticeTreatmentList } from '../../redux/slices/practiceTreatment';
// Dentally — lightweight imports direct, heavy component lazy
import { DENTALLY_REASON_OPTIONS, parseDentallyDate } from '../../utils/dentallyUtils';
import { prefetchDentallyPractitioners } from '../../redux/slices/dentallyPractitioners';
import { useDentallyBooking } from '../../hooks/useDentallyBooking';
import { closeWhatsappConversationWithReason } from '../../redux/slices/whatsappV2';
import {
    filterPracticeUserSelections,
    selectPracticeUsersForPractice,
} from '../../redux/slices/practiceUser';
import useDentallyPatientLink from '../../hooks/useDentallyPatientLink';
import { getSalutationOptions } from '../../utils/getReferralData';
import { hasPractitionerRole } from '../../utils/practiceUserRoles';

const DentallyAvailabilityStep = lazyWithRetries(() => import('../../components/dentally/DentallyAvailabilityStep'));

// ── Shared styles ────────────────────────────────────────────────────
const RADIUS = '8px';
const LABEL_SX = {
    color: '#919EAB', fontSize: '11px', textTransform: 'uppercase',
    letterSpacing: '0.5px', fontWeight: 600, mb: 0.5, display: 'block',
};
const SECTION_BORDER = '1px solid rgba(145,158,171,0.08)';
const COMPACT_INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        borderRadius: RADIUS, fontSize: '12px', height: 32,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16)' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.3)' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00A76F', borderWidth: '1px' },
    },
    '& .MuiInputBase-input': { py: '6px' },
};
const COMPACT_SELECT_SX = {
    borderRadius: RADIUS, fontSize: '12px', height: 32,
    '& .MuiSelect-select': { py: '6px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.3)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00A76F', borderWidth: '1px' },
};

const QUICK_PRESETS = [
    { label: 'Tomorrow 9am', getValue: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0); return d; } },
    { label: 'Mon 9am', getValue: () => { const d = new Date(); const day = d.getDay(); const diff = day <= 1 ? 1 - day + 7 : 8 - day; d.setDate(d.getDate() + diff); d.setHours(9, 0, 0, 0); return d; } },
    { label: '1 week', getValue: () => { const d = new Date(); d.setDate(d.getDate() + 7); d.setHours(9, 0, 0, 0); return d; } },
    { label: '2 weeks', getValue: () => { const d = new Date(); d.setDate(d.getDate() + 14); d.setHours(9, 0, 0, 0); return d; } },
];

const FALLBACK_SALUTATION_OPTIONS = getSalutationOptions().filter(option => option.value);
const ROLE_AVATAR_COLORS = {
    PRACTITIONER: { border: '#078DEE', bg: 'rgba(7,141,238,0.08)', text: '#078DEE' },
    'TCO/USER': { border: '#855CF8', bg: 'rgba(133,92,248,0.08)', text: '#855CF8' },
};
const getRoleColor = (role, isSelected) => {
    if (isSelected) return { border: '#00A76F', bg: 'rgba(0, 167, 111, 0.06)', text: '#00A76F' };
    const key = role?.toUpperCase();
    const c = ROLE_AVATAR_COLORS[key] || ROLE_AVATAR_COLORS['TCO/USER'];
    return { border: 'transparent', bg: c.bg, text: c.text };
};

// ── Component ────────────────────────────────────────────────────────
export default function BookingPanel({ open, onClose, contactDetails, practiceId, taskId, onShareMessage, embedded: embeddedInResolve, onBooked }) {
    const dispatch = useDispatch();
    const { user: currentUser } = useAuth();
    const {
        appointmentMethod: appointmentMethods,
        appointmentType: appointmentTypes,
        salutationOptions: schemaSalutationOptions,
    } = useSelector((state) => state.schema);
    const salutationOptions = schemaSalutationOptions?.filter(option => option.value);
    const availableSalutationOptions = salutationOptions?.length === FALLBACK_SALUTATION_OPTIONS.length
        ? salutationOptions
        : FALLBACK_SALUTATION_OPTIONS;
    const practiceUsers = useSelector((state) => selectPracticeUsersForPractice(state, practiceId));
    const practiceTreatments = useSelector((state) => state.practiceTreatment?.treatmentsList?.results || []);
    const whatsAppLeadDetails = useSelector((state) => state.whatsappV2?.whatsAppChatMetaData?.leadDetails);
    const dentallyEnabled = useSelector((state) => state.practice?.dentallyEnabledGlobal);
    const whatsAppThread = useSelector((state) => state.whatsappV2?.whatsAppChatMetaData?.thread);
    // Same field ChatPage keys "received" off of (`arePhoneNumsEqual(message.from_waid,
    // whatsAppChatMetaData.phoneNumber)`) — NOT `thread.phone_number`, which doesn't
    // exist on the thread serializer.
    const whatsAppPhoneNumber = useSelector((state) => state.whatsappV2?.whatsAppChatMetaData?.phoneNumber);
    // `whatsappChats` is an ARRAY of raw message objects for the currently open
    // thread (see whatsappV2 slice initialState), not a map keyed by phone number.
    const whatsappChats = useSelector((state) => state.whatsappV2?.whatsappChats);

    // Check if 24hr WhatsApp window is expired — reuses the exact same
    // "last inbound message" lookup and `isWithin24Hours` check ChatPage uses
    // to gate its own composer, so the booking panel and the chat composer
    // never disagree about whether free-text send is still allowed.
    const isWindowExpired = useMemo(() => {
        const lastInboundCreatedAt = getLastInboundMessageCreatedAt(whatsappChats, whatsAppPhoneNumber);
        if (!lastInboundCreatedAt) return true; // No inbound = expired
        return !isWithin24Hours(lastInboundCreatedAt);
    }, [whatsappChats, whatsAppPhoneNumber]);

    // ── CRM state ──
    const [appointmentDate, setAppointmentDate] = useState(null);
    const [appointmentMethod, setAppointmentMethod] = useState('IN_PRACTICE');
    const [appointmentType, setAppointmentType] = useState('CONSULTATION');
    const [appointmentAssignee, setAppointmentAssignee] = useState(null);
    const [notes, setNotes] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [bookingSuccess, setBookingSuccess] = useState(false);
    // Past-date CRM booking — the backend auto-marks it ATTENDED on creation
    // (apps/appointments/receivers.py::in_past), so this just tracks whether
    // that was confirmed for the success-screen message.
    const [pastDateAttended, setPastDateAttended] = useState(false);
    const [showDetails, setShowDetails] = useState(null);
    const [showMethodPicker, setShowMethodPicker] = useState(false);
    const [editPhone, setEditPhone] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editAddress, setEditAddress] = useState('');
    const [editingTreatment, setEditingTreatment] = useState(false);
    const [selectedTreatmentId, setSelectedTreatmentId] = useState(null);
    const [bookedWith, setBookedWith] = useState(null);
    const [teethCount, setTeethCount] = useState('');
    const [editSalutation, setEditSalutation] = useState('');
    const [editFirstName, setEditFirstName] = useState('');
    const [editLastName, setEditLastName] = useState('');
    const [editDob, setEditDob] = useState('');
    const [fetchedTreatments, setFetchedTreatments] = useState(null);

    // ── Dentally state ──
    const [useDentally, setUseDentally] = useState(false);
    const [dentallySlot, setDentallySlot] = useState(null);
    const [dentallyAssignees, setDentallyAssignees] = useState([]);
    const [dentallyReason, setDentallyReason] = useState('Consultation');
    const [dentallyAvailSearch, setDentallyAvailSearch] = useState(null);

    const [detailSaveStatus, setDetailSaveStatus] = useState(null);
    const [isClosing, setIsClosing] = useState(false);
    const saveTimerRef = useRef(null);
    const lastSavedNoteRef = useRef('');
    const bookingScopeGenerationRef = useRef(0);
    const dentallyUiGenerationRef = useRef(0);

    // Data — must be declared before useCallback/useMemo that reference them
    const leadDetails = whatsAppLeadDetails || contactDetails?.leadDetails;
    const leadId = leadDetails?.id;
    const dentallyUiScopeKey = `${practiceId ?? ''}:${leadId ?? ''}:${open ? 'open' : 'closed'}`;
    const dentallyUiScopeRef = useRef(dentallyUiScopeKey);
    if (dentallyUiScopeRef.current !== dentallyUiScopeKey) {
        dentallyUiScopeRef.current = dentallyUiScopeKey;
        dentallyUiGenerationRef.current += 1;
    }
    // lead_treatments may be missing in embedded/To-Do context — try all sources
    const treatmentId = leadDetails?.lead_treatments?.[0]?.id
        || leadDetails?.lead_treatment?.id
        || contactDetails?.leadDetails?.lead_treatments?.[0]?.id
        || contactDetails?.leadDetails?.lead_treatment?.id
        || fetchedTreatments?.[0]?.id;
    const leadPhone = leadDetails?.phone_number || leadDetails?.lead_contact?.phone || '';
    const leadEmail = leadDetails?.lead_contact?.email || leadDetails?.email || '';
    const leadAddress = leadDetails?.lead_address?.address_line_1 || '';

    // Build staff list using practice-user ID (el.id), NOT user.id
    // The appointments API expects practice-user IDs for assignees
    const allStaff = useMemo(() => {
        if (!practiceUsers?.length) return [];
        return practiceUsers
            // A dual-role ADMIN + Practitioner stays bookable as a practitioner.
            .filter(el => el?.is_enabled && (hasPractitionerRole(el) || (el?.role !== 'ADMIN' && el?.role !== 'SUPPORT')) && !el?.user?.is_superuser)
            .map(el => {
                let role = el?.role;
                if (hasPractitionerRole(el)) role = 'Practitioner';
                else if (el?.role === 'TCO' || el?.role === 'USER') role = 'TCO/USER';
                return {
                    id: el.id, // practice-user ID — this is what the API expects
                    name: `${el?.user?.first_name || ''} ${el?.user?.last_name || ''}`.trim(),
                    role,
                    avatar: el?.user?.avatar,
                };
            });
    }, [practiceUsers]);
    const assignableUsers = useMemo(() => allStaff.filter(u => u.role === 'TCO/USER'), [allStaff]);
    const practitioners = useMemo(() => allStaff.filter(u => u.role?.toUpperCase() === 'PRACTITIONER'), [allStaff]);
    const bookableStaff = practitioners.length > 0 ? practitioners : allStaff;
    const updateDentallySlotForScope = useCallback((nextSlot) => {
        if (dentallyUiScopeRef.current === dentallyUiScopeKey) setDentallySlot(nextSlot);
    }, [dentallyUiScopeKey]);
    const updateDentallyAssigneesForScope = useCallback((nextAssignees) => {
        if (dentallyUiScopeRef.current === dentallyUiScopeKey) setDentallyAssignees(nextAssignees);
    }, [dentallyUiScopeKey]);
    const updateDentallySearchForScope = useCallback((nextSearch) => {
        if (dentallyUiScopeRef.current === dentallyUiScopeKey) setDentallyAvailSearch(nextSearch);
    }, [dentallyUiScopeKey]);

    // Auto-save detail fields after 2s — real API call
    const triggerAutoSave = useCallback((overrides = {}) => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        setDetailSaveStatus(null);
        saveTimerRef.current = setTimeout(async () => {
            if (!leadId || !practiceId) return;
            setDetailSaveStatus('saving');
            const currentDob = Object.prototype.hasOwnProperty.call(overrides, 'dob')
                ? overrides.dob
                : editDob;
            const currentSalutation = Object.prototype.hasOwnProperty.call(overrides, 'salutation')
                ? overrides.salutation
                : editSalutation;
            const payload = {
                id: leadId,
                lead_contact: {
                    phone: editPhone || undefined,
                    email: editEmail || undefined,
                },
                lead_address: {
                    address_line_1: editAddress || undefined,
                },
                ...(editFirstName && { first_name: editFirstName }),
                ...(editLastName && { last_name: editLastName }),
                ...(currentSalutation && { salutation: currentSalutation }),
                ...(currentDob && { dob: currentDob }),
            };
            const res = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`, 'patch', payload);
            if (res?.ok) {
                setDetailSaveStatus('saved');
                setTimeout(() => { setDetailSaveStatus(null); setShowDetails(null); }, 800);
            } else {
                setDetailSaveStatus('error');
                setTimeout(() => setDetailSaveStatus(null), 2000);
            }
        }, 2000);
    }, [leadId, practiceId, editPhone, editEmail, editAddress, editFirstName, editLastName, editSalutation, editDob]);

    useEffect(() => () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); }, []);

    // Save treatment change (different API endpoint)
    const saveTreatmentChange = useCallback(async (newTreatmentId, newTeethCount) => {
        if (!leadId || !practiceId || !treatmentId) return;
        setDetailSaveStatus('saving');
        const payload = {};
        if (newTreatmentId && newTreatmentId !== treatmentId) {
            payload.treatment = newTreatmentId;
        }
        if (newTeethCount !== undefined) {
            payload.leadtreatment_specification = { lead_treatment: treatmentId, teeth_count: Number(newTeethCount) || 0 };
        }
        if (Object.keys(payload).length === 0) { setDetailSaveStatus(null); return; }
        const res = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${treatmentId}/`, 'patch', payload);
        if (res?.ok) {
            setDetailSaveStatus('saved');
            setTimeout(() => setDetailSaveStatus(null), 1500);
        } else {
            setDetailSaveStatus('error');
            setTimeout(() => setDetailSaveStatus(null), 2000);
        }
    }, [leadId, practiceId, treatmentId]);

    // Fetch treatments if not available from lead data
    useEffect(() => {
        bookingScopeGenerationRef.current += 1;
        const generation = bookingScopeGenerationRef.current;
        if (open && leadId && practiceId && !treatmentId) {
            callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/?fields={lead_treatments{id,treatment,status,custom_price,price,leadtreatment_specification}}`)
                .then(res => {
                    if (generation === bookingScopeGenerationRef.current && res?.ok && res?.data?.lead_treatments?.length) {
                        setFetchedTreatments(res.data.lead_treatments);
                    }
                });
        }
        if (!open) setFetchedTreatments(null);
        return () => {
            if (generation === bookingScopeGenerationRef.current) bookingScopeGenerationRef.current += 1;
        };
    }, [open, leadId, practiceId, treatmentId]);

    const currentLeadTreatment = leadDetails?.lead_treatments?.[0] || fetchedTreatments?.[0];
    const activeTreatmentId = selectedTreatmentId || treatmentId;
    const activeTreatment = selectedTreatmentId
        ? practiceTreatments.find(t => t.id === selectedTreatmentId)
        : currentLeadTreatment;

    const rawTreatment = currentLeadTreatment?.treatment;
    const currentTreatmentName = currentLeadTreatment?.treatment_name
        || rawTreatment?.name || rawTreatment?.treatment_item?.name
        || (typeof rawTreatment === 'string' ? rawTreatment : null)
        || currentLeadTreatment?.treatment_data?.name || '—';
    const treatmentName = selectedTreatmentId
        ? (activeTreatment?.treatment_item?.name || activeTreatment?.treatment || activeTreatment?.name || '—')
        : currentTreatmentName;
    const treatmentPrice = selectedTreatmentId
        ? (activeTreatment?.price || '—')
        : (currentLeadTreatment?.custom_price || currentLeadTreatment?.price || '—');
    const needsTeethCount = /bonding|implant|veneer|crown|bridge/i.test(treatmentName);

    // Pre-booking status check
    const leadStatus = currentLeadTreatment?.status;
    const existingAppointment = currentLeadTreatment?.appointments?.[0];
    const isAlreadyBooked = leadStatus && leadStatus !== 'NEW';
    const [showBookingForm, setShowBookingForm] = useState(false);
    const [confirmationSent, setConfirmationSent] = useState({}); // { whatsapp: true, email: true }
    const [confirmPreview, setConfirmPreview] = useState(null); // null | 'whatsapp' | 'email'
    const [treatmentInfoOpen, setTreatmentInfoOpen] = useState(false);
    const [personalEmailBody, setPersonalEmailBody] = useState('');
    const [showTimeline, setShowTimeline] = useState(false);
    const [editableMessage, setEditableMessage] = useState('');
    const statusInfo = isAlreadyBooked ? {
        CONSULTATION_BOOKED: { label: 'Consultation Booked', detail: existingAppointment ? `${treatmentName} on ${moment(existingAppointment.datetime, 'DD-MM-YYYY HH:mm:ss').format('ddd D MMM YYYY, HH:mm')}` : treatmentName },
        CONSULTATION_ATTENDED: { label: 'Consultation Attended', detail: existingAppointment ? `Attended on ${moment(existingAppointment.datetime, 'DD-MM-YYYY HH:mm:ss').format('ddd D MMM YYYY')}` : '' },
        TREATMENT_STARTED: { label: 'Treatment Started', detail: treatmentName },
        CLOSED: { label: 'Treatment Completed', detail: treatmentName },
    }[leadStatus] || null : null;

    const {
        isLoading: patientLinkLoading,
        outcome: patientLinkOutcome,
    } = useDentallyPatientLink(practiceId, leadId);

    // Dentally booking hook — declared after all data dependencies
    const dentallyBooking = useDentallyBooking({
        practiceId,
        existingLead: leadDetails,
        existingTreatmentId: activeTreatmentId,
        isPaymentRequired: false,
        patientLinkOutcome,
    });

    useEffect(() => {
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        setUseDentally(false);
        setDentallySlot(null);
        setDentallyAssignees([]);
        setDentallyReason('Consultation');
        setDentallyAvailSearch(null);
        setNotes('');
        setBookingSuccess(false);
        setPastDateAttended(false);
        setShowDetails(null);
        setFetchedTreatments(null);
        setSelectedTreatmentId(null);
        setTeethCount('');
        setAppointmentAssignee(null);
        setBookedWith(null);
        dentallyBooking.reset();
    }, [practiceId, leadId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Seed on open, reset on close
    useEffect(() => {
        if (open) {
            setEditFirstName(leadDetails?.first_name || '');
            setEditLastName(leadDetails?.last_name || '');
            setEditPhone(leadPhone);
            setEditEmail(leadEmail);
            setEditAddress(leadAddress);
            setEditSalutation(leadDetails?.salutation?.toUpperCase() || '');
            setEditDob(leadDetails?.dob || '');
            const existingTeeth = leadDetails?.lead_treatments?.[0]?.leadtreatment_specification?.teeth_count;
            if (existingTeeth) setTeethCount(String(existingTeeth));
            // Default assignee to current user's practice-user ID
            if (currentUser?.id) {
                const currentPracticeUser = practiceUsers?.find(pu => pu?.user?.id === currentUser.id);
                setAppointmentAssignee(currentPracticeUser?.id || null);
            }
            // Fetch practice treatments if not loaded
            if (practiceId && practiceTreatments.length === 0) {
                dispatch(getPracticeTreatmentList(practiceId, { limit: 100, offset: 0 }));
            }
        } else {
            setAppointmentDate(null); setAppointmentMethod('IN_PRACTICE'); setAppointmentType('CONSULTATION');
            setAppointmentAssignee(null); setNotes(''); setIsSaving(false); setBookingSuccess(false);
            setPastDateAttended(false);
            setShowDetails(null); setDetailSaveStatus(null); setIsClosing(false); setShowBookingForm(false); setConfirmationSent({}); setConfirmPreview(null); setEditableMessage(''); setTreatmentInfoOpen(false); setShowTimeline(false); setPersonalEmailBody('');
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
            setShowMethodPicker(false); setEditPhone(''); setEditEmail(''); setEditAddress('');
            setEditingTreatment(false); setSelectedTreatmentId(null); setBookedWith(null); setTeethCount('');
            setUseDentally(false); setDentallySlot(null); setDentallyAssignees([]); setDentallyReason('Consultation'); setDentallyAvailSearch(null);
            dentallyBooking.reset();
            setEditSalutation(''); setEditDob(''); setEditFirstName(''); setEditLastName('');
        }
    }, [open, leadId, leadPhone, leadEmail, leadAddress]);

    // ── CRM booking handler ──
    const handleBookCRM = async () => {
        if (!appointmentDate || !leadId || !bookedWith || !activeTreatmentId || isSaving) return;
        setIsSaving(true);
        // Computed at submit time from the raw picker Date — not parsed from an
        // already-formatted API string.
        const isPast = new Date(appointmentDate).getTime() < Date.now();
        const payload = {
            datetime: `${moment(appointmentDate).format('DD-MM-YYYY HH:mm:ss')}.0Z`,
            method: appointmentMethod || 'IN_PRACTICE',
            type: appointmentType || 'CONSULTATION',
            contacted_via: 'WHATSAPP',
            note: notes || '',
            assignees: [...new Set([appointmentAssignee, bookedWith].filter(Boolean))],
            lead_treatment: activeTreatmentId,
        };
        const res = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/appointments/`, 'post', payload);
        if (res?.ok && res?.status === 201) {
            if (isPast) {
                // BE (apps/appointments/receivers.py::in_past) auto-marks a
                // past-dated appointment ATTENDED synchronously on creation —
                // there's no "did the patient attend?" question to ask, and
                // to_failed_to_attend's FSM source excludes ATTENDED so a "No"
                // path could never actually apply. Verify the create response
                // already reflects that; if not (e.g. clock skew), re-check
                // once rather than claiming attended when we can't confirm it.
                let attended = res?.data?.status === 'ATTENDED';
                if (!attended && res?.data?.id) {
                    const check = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/appointments/${res.data.id}/`, 'get');
                    attended = !!(check?.ok && check?.data?.status === 'ATTENDED');
                }
                setPastDateAttended(attended);
            }
            setIsSaving(false);
            setBookingSuccess(true);
            // No auto-close — success screen is now an action hub
        } else {
            setIsSaving(false);
            console.warn('[BookingPanel] CRM booking failed:', res?.data);
        }
    };

    // ── Dentally booking handler ──
    const handleBookDentally = async () => {
        if (!dentallySlot || !leadId || dentallyBooking.loading) return;
        const submittedScope = dentallyUiScopeKey;
        const submittedGeneration = dentallyUiGenerationRef.current;
        const startIso = parseDentallyDate(dentallySlot.start_time);
        const endIso = parseDentallyDate(dentallySlot.finish_time);
        const durationMinutes = Math.round((endIso - startIso) / 60000);
        try {
            const submitOutcome = await dentallyBooking.submit({
                practitionerId: dentallySlot._queried_practitioner_id || dentallySlot.practitioner_id,
                reason: dentallyReason,
                dateTime: new Date(startIso),
                durationMinutes,
                method: 'IN_PRACTICE',
                assignees: filterPracticeUserSelections(
                    practiceUsers,
                    dentallyAssignees.map((u) => (typeof u === 'object' ? u : { id: u })),
                ).map((u) => u.id),
                notes,
                paymentOption: 'none',
                ...(editSalutation && { salutation: editSalutation }),
                ...(editDob && { dob: editDob }),
            });
            if (
                submitOutcome?.ok === true
                && dentallyUiScopeRef.current === submittedScope
                && dentallyUiGenerationRef.current === submittedGeneration
            ) {
                setBookingSuccess(true);
                // No auto-close — success screen is now an action hub
            }
        } catch {
            console.error('[BookingPanel] Dentally booking failed');
        }
    };

    const handleBook = useDentally ? handleBookDentally : handleBookCRM;
    const isLoading = useDentally ? dentallyBooking.loading : isSaving;

    const buildBookingMessage = () => {
        const firstName = leadDetails?.first_name || 'there';
        const apptDate = appointmentDate || (existingAppointment?.datetime ? moment(existingAppointment.datetime, 'DD-MM-YYYY HH:mm:ss').toDate() : null);
        const date = apptDate ? moment(apptDate).format('dddd Do MMMM') : '';
        const time = apptDate ? moment(apptDate).format('HH:mm') : '';
        const practitionerName = bookableStaff.find(u => u.id === bookedWith)?.name || '';
        return `Hi ${firstName}, your ${treatmentName} appointment is booked for ${date} at ${time}${practitionerName ? ` with ${practitionerName}` : ''}. See you then!`;
    };

    const buildTreatmentInfoMessage = () => {
        const firstName = leadDetails?.first_name || 'there';
        const price = treatmentPrice && treatmentPrice !== '—' ? `\n\nPrice: £${treatmentPrice}` : '';
        const description = currentLeadTreatment?.treatment_data?.description || currentLeadTreatment?.treatment?.description || '';
        const descLine = description ? `\n\n${description.replace(/<[^>]*>/g, '').slice(0, 200)}` : '';
        const offers = currentLeadTreatment?.treatment_data?.offers || [];
        const offerLine = offers.length > 0 ? `\n\nCurrent offer: ${offers[0]?.name || ''}` : '';
        return `Hi ${firstName}, here's some information about ${treatmentName}:${price}${descLine}${offerLine}\n\nWould you like to go ahead and book an appointment? Let me know if you have any questions.`;
    };

    const buildEmailSubject = () => `Appointment Confirmation — ${treatmentName}`;

    const buildEmailBody = () => {
        const firstName = leadDetails?.first_name || 'there';
        const apptDate = appointmentDate || (existingAppointment?.datetime ? moment(existingAppointment.datetime, 'DD-MM-YYYY HH:mm:ss').toDate() : null);
        const date = apptDate ? moment(apptDate).format('dddd Do MMMM YYYY') : '';
        const time = apptDate ? moment(apptDate).format('HH:mm') : '';
        const practitionerName = bookableStaff.find(u => u.id === bookedWith)?.name || '';
        return `Dear ${firstName},\n\nThis is to confirm your ${treatmentName} appointment:\n\nDate: ${date}\nTime: ${time}${practitionerName ? `\nWith: ${practitionerName}` : ''}\n\nIf you need to reschedule, please let us know.\n\nKind regards`;
    };

    const [emailSending, setEmailSending] = useState(false);

    const handleSendEmail = async () => {
        const email = editEmail || leadDetails?.lead_contact?.email || leadDetails?.email;
        if (!email || !practiceId || !leadId) return;
        setEmailSending(true);
        const body = personalEmailBody || buildEmailBody();
        const res = await callApi(`/api/v1/practices/${practiceId}/transactional-messages/`, 'post', {
            detail: { subject: buildEmailSubject(), body, to: email, lead: leadId },
        });
        if (res?.ok && res?.data?.id) {
            await callApi(`/api/v1/practices/${practiceId}/transactional-messages/${res.data.id}/schedule/`, 'post', {});
            setConfirmationSent(prev => ({ ...prev, email: true }));
            setConfirmPreview('email');
        }
        setEmailSending(false);
    };

    const handleCopyMessage = () => {
        const text = editableMessage || buildBookingMessage();
        navigator.clipboard.writeText(text);
        setConfirmationSent(prev => ({ ...prev, copied: true }));
        setTimeout(() => setConfirmationSent(prev => ({ ...prev, copied: false })), 2000);
    };

    const handleDone = async () => {
        setIsClosing(true);
        // When embedded in ResolvePanel, the parent owns checklist-complete +
        // thread-close ordering via onBooked — do not close the thread here.
        // onClose() is intentionally skipped: ResolvePanel owns dismissal via
        // onConfirmClose once its complete→close steps finish.
        if (onBooked) {
            setIsClosing(false);
            onBooked();
            return;
        }
        // Standalone (non-embedded) path: close thread directly as before.
        const threadId = whatsAppThread?.id;
        if (threadId && leadId) {
            dispatch(closeWhatsappConversationWithReason(threadId, 'Booked', leadId));
        }
        // Complete To-Do task if opened from To-Do
        if (taskId && practiceId) {
            await callApi(`/api/v1/practices/${practiceId}/reminders/${taskId}/`, 'patch', { is_completed: true });
        }
        setIsClosing(false);
        onClose();
    };

    // Progress steps — mode-aware (only show incomplete items the user needs to act on)
    const bookingSteps = useDentally ? [
        { label: 'Select slot', done: !!dentallySlot },
        ...(patientLinkOutcome === 'unique' ? [] : [
            { label: 'Title', done: !!editSalutation },
            { label: 'Date of birth', done: !!editDob },
        ]),
        { label: 'Reason', done: !!dentallyReason },
    ] : [
        { label: 'Date & time', done: !!appointmentDate },
        { label: 'Booked with', done: !!bookedWith },
        { label: 'Treatment', done: !!activeTreatmentId },
    ];
    const completedSteps = bookingSteps.filter(s => s.done).length;
    const totalSteps = bookingSteps.length;
    const allStepsComplete = completedSteps === totalSteps;
    const patientLinkResolved = patientLinkOutcome === 'unique' || patientLinkOutcome === 'none';
    const isBookDisabled = !allStepsComplete || !leadId || isLoading || patientLinkLoading || !patientLinkResolved;
    const remainingSteps = bookingSteps.filter(s => !s.done);

    // ── Render ───────────────────────────────────────────────────────
    return (
        <Box sx={embeddedInResolve ? { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 } : {
            width: open ? 360 : 0, transition: 'width 300ms ease', overflow: 'hidden',
            flexShrink: 0, borderLeft: open ? '1px solid rgba(145,158,171,0.2)' : 'none', height: '100%',
        }}>
            <Box sx={embeddedInResolve ? { flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#fff', minHeight: 0 } : { width: 360, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
                {/* Header — skip when embedded in ResolvePanel */}
                {!embeddedInResolve && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, borderBottom: '1px solid rgba(145,158,171,0.12)', flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {showBookingForm && isAlreadyBooked && (
                            <IconButton size="small" onClick={() => setShowBookingForm(false)} sx={{ width: 24, height: 24, mr: 0.25 }}>
                                <Icon icon="eva:arrow-back-fill" width={16} />
                            </IconButton>
                        )}
                        <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#212B36' }}>Book</Typography>
                        {dentallyEnabled && (
                            <Box sx={{ display: 'flex', gap: 0.375 }}>
                                {[
                                    { label: 'CRM', value: false },
                                    { label: 'Dentally', value: true },
                                ].map((mode) => (
                                    <Box key={mode.label} onClick={() => {
                                        setUseDentally(mode.value);
                                        if (mode.value && practiceId) dispatch(prefetchDentallyPractitioners(practiceId));
                                    }}
                                        sx={{
                                            px: 0.75, py: 0.25, borderRadius: '10px', fontSize: '10px', fontWeight: 600, cursor: 'pointer',
                                            border: '1px solid', borderColor: useDentally === mode.value ? '#00A76F' : '#e0e0e0',
                                            color: useDentally === mode.value ? '#00A76F' : '#919EAB',
                                            background: useDentally === mode.value ? 'rgba(0, 167, 111, 0.06)' : 'transparent',
                                            transition: 'all 150ms ease', '&:hover': { borderColor: '#919EAB' },
                                        }}>
                                        {mode.label}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ width: 28, height: 28 }}><Icon icon="eva:close-fill" width={18} /></IconButton>
                </Box>
                )}

                {/* Content */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 2.5, py: 2, minHeight: 0 }}>
                    {/* CRM/Dentally toggle — shown inline when embedded in ResolvePanel */}
                    {embeddedInResolve && dentallyEnabled && (
                        <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5 }}>
                            {[
                                { label: 'CRM', value: false },
                                { label: 'Dentally', value: true },
                            ].map((mode) => (
                                <Box key={mode.label} onClick={() => {
                                    setUseDentally(mode.value);
                                    if (mode.value && practiceId) dispatch(prefetchDentallyPractitioners(practiceId));
                                }}
                                    sx={{
                                        px: 1.25, py: 0.375, borderRadius: '14px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                                        border: '1px solid', borderColor: useDentally === mode.value ? '#00A76F' : '#e0e0e0',
                                        color: useDentally === mode.value ? '#00A76F' : '#637381',
                                        background: useDentally === mode.value ? 'rgba(0, 167, 111, 0.06)' : 'transparent',
                                        transition: 'all 150ms ease', '&:hover': { borderColor: '#919EAB', background: '#fafafa' },
                                    }}>
                                    {mode.label}
                                </Box>
                            ))}
                        </Box>
                    )}
                    {/* eslint-disable-next-line no-nested-ternary */}
                    {bookingSuccess ? (
                        <Box sx={{ py: 3 }}>
                            {/* Success header */}
                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                <Icon icon="akar-icons:circle-check-fill" width={40} style={{ color: '#00A76F', marginBottom: 8 }} />
                                <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#007B55' }}>
                                    {pastDateAttended ? 'Past-date booking recorded — marked as attended.' : 'Appointment Booked'}
                                </Typography>
                                <Typography sx={{ fontSize: '12px', color: '#637381', mt: 0.5 }}>
                                    {appointmentDate ? moment(appointmentDate).format('ddd D MMM YYYY, HH:mm') : ''} &middot; {(appointmentMethods?.find(m => m.value === appointmentMethod)?.label) || 'In Practice'}
                                </Typography>
                                <Typography sx={{ fontSize: '12px', color: '#919EAB' }}>
                                    {treatmentName}{bookedWith ? ` · ${bookableStaff.find(u => u.id === bookedWith)?.name || ''}` : ''}
                                </Typography>
                            </Box>

                            {/* Action buttons */}
                            <Stack spacing={1}>
                                {onShareMessage && (
                                    <Button
                                        variant="outlined" fullWidth
                                        startIcon={<Icon icon="ri:whatsapp-fill" width={16} />}
                                        onClick={() => onShareMessage(buildBookingMessage())}
                                        sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600, borderRadius: RADIUS, borderColor: '#00A76F', color: '#00A76F', '&:hover': { borderColor: '#007B55', background: 'rgba(0, 167, 111, 0.04)' } }}
                                    >
                                        Share booking details
                                    </Button>
                                )}
                                <Button
                                    variant="text" fullWidth
                                    startIcon={<Icon icon="eva:external-link-fill" width={16} />}
                                    onClick={() => window.open(`/dashboard/practice/${practiceId}/lead-detail/${leadId}`, '_blank')}
                                    sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 500, borderRadius: RADIUS, color: '#637381' }}
                                >
                                    View lead
                                </Button>
                                <Button
                                    variant="contained" fullWidth
                                    onClick={handleDone}
                                    disabled={isClosing}
                                    startIcon={isClosing ? <CircularProgress size={14} color="inherit" /> : <Icon icon="eva:checkmark-circle-2-fill" width={16} />}
                                    sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600, borderRadius: RADIUS, background: '#00A76F', '&:hover': { background: '#007B55' }, boxShadow: 'none', py: 1 }}
                                >
                                    {isClosing ? 'Closing...' : 'Done — close & resolve'}
                                </Button>
                            </Stack>
                        </Box>
                    ) : isAlreadyBooked && !showBookingForm ? (
                        /* ── Already booked prompt ── */
                        <Box sx={{ py: 3 }}>
                            <Box sx={{ textAlign: 'center', mb: 3 }}>
                                <Icon icon="eva:checkmark-circle-2-fill" width={36} style={{ color: '#078DEE', marginBottom: 8 }} />
                                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#212B36', mb: 0.5 }}>
                                    {statusInfo?.label || 'Already Booked'}
                                </Typography>
                                {statusInfo?.detail && (
                                    <Typography sx={{ fontSize: '12px', color: '#637381' }}>{statusInfo.detail}</Typography>
                                )}
                            </Box>

                            <Typography sx={{ fontSize: '13px', color: '#637381', textAlign: 'center', mb: 3, px: 2 }}>
                                Do you need to book another appointment for {leadDetails?.first_name || 'this patient'}?
                            </Typography>

                            <Stack spacing={1}>
                                {/* Send booking confirmation — channel picker + preview */}
                                <Box sx={{ mb: 0.5 }}>
                                    <Typography sx={{ ...LABEL_SX, mb: 0.75 }}>Send booking confirmation</Typography>

                                    {/* Channel buttons */}
                                    <Box sx={{ display: 'flex', gap: 0.5, mb: confirmPreview ? 1 : 0 }}>
                                        {[
                                            { key: 'whatsapp', label: 'WhatsApp', icon: 'ri:whatsapp-fill', color: '#25D366' },
                                            { key: 'email', label: 'Email', icon: 'eva:email-fill', color: '#078DEE' },
                                        ].map((ch) => {
                                            const isSent = confirmationSent[ch.key];
                                            const isActive = confirmPreview === ch.key;
                                            const variant = isSent ? 'contained' : 'outlined';
                                            return (
                                                <Button key={ch.key} variant={variant} size="small"
                                                    onClick={() => {
                                                        if (isSent) return;
                                                        setConfirmPreview(isActive ? null : ch.key);
                                                        setEditableMessage(ch.key === 'email' ? buildEmailBody() : buildBookingMessage());
                                                    }}
                                                    disabled={!!isSent}
                                                    startIcon={<Icon icon={isSent ? 'eva:checkmark-circle-2-fill' : ch.icon} width={14} />}
                                                    sx={{
                                                        flex: 1, textTransform: 'none', fontSize: '11px', fontWeight: 600,
                                                        borderRadius: RADIUS, py: 0.75,
                                                        ...(isSent // eslint-disable-line no-nested-ternary
                                                            ? { background: ch.color, color: '#fff', borderColor: ch.color, '&.Mui-disabled': { background: ch.color, color: '#fff', opacity: 0.85 } }
                                                            : isActive
                                                                ? { borderColor: ch.color, color: ch.color, background: `${ch.color}08` }
                                                                : { borderColor: '#e0e0e0', color: '#637381', '&:hover': { borderColor: ch.color, color: ch.color, background: `${ch.color}0A` } }
                                                        ),
                                                    }}
                                                >
                                                    {isSent ? 'Sent' : ch.label}
                                                </Button>
                                            );
                                        })}
                                    </Box>

                                    {/* WhatsApp edit dialog — matches AI wizard style */}
                                    <Dialog
                                        open={confirmPreview === 'whatsapp'}
                                        onClose={() => setConfirmPreview(null)}
                                        maxWidth="sm" fullWidth
                                        TransitionComponent={undefined}
                                        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' } }}
                                    >
                                        {/* Gradient header */}
                                        <Box sx={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Icon icon="ri:whatsapp-fill" width={20} style={{ color: '#fff' }} />
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '15px' }}>Booking Confirmation</Typography>
                                                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>Edit message before sending</Typography>
                                                </Box>
                                            </Box>
                                            <IconButton onClick={() => setConfirmPreview(null)} sx={{ color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
                                                <Icon icon="eva:close-fill" width={20} />
                                            </IconButton>
                                        </Box>

                                        {/* Editor */}
                                        <Box sx={{ p: 3 }}>
                                            <TextField
                                                multiline minRows={4} maxRows={10} fullWidth autoFocus
                                                value={editableMessage}
                                                onChange={(e) => setEditableMessage(e.target.value)}
                                                placeholder="Edit your message..."
                                                onKeyDown={(e) => {
                                                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !isWindowExpired) {
                                                        if (onShareMessage) onShareMessage(editableMessage);
                                                        setConfirmationSent(prev => ({ ...prev, whatsapp: true }));
                                                        setConfirmPreview(null);
                                                    }
                                                }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 2, fontSize: '14px', lineHeight: 1.6,
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#25D366' },
                                                    },
                                                }}
                                            />
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
                                                {/* Character count + status */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography sx={{ fontSize: '11px', color: (editableMessage?.length || 0) > 4096 ? '#FF5630' : '#919EAB' }}>
                                                        {editableMessage?.length || 0} / 4096
                                                    </Typography>
                                                    {editableMessage !== buildBookingMessage() && (
                                                        <Tooltip title="Reset to original" arrow>
                                                            <IconButton size="small" onClick={() => setEditableMessage(buildBookingMessage())} sx={{ width: 24, height: 24 }}>
                                                                <Icon icon="eva:undo-fill" width={14} style={{ color: '#919EAB' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                                {/* Copy */}
                                                <Button size="small" variant="text"
                                                    startIcon={<Icon icon={confirmationSent.copied ? 'eva:checkmark-fill' : 'eva:copy-fill'} width={14} />}
                                                    onClick={handleCopyMessage}
                                                    sx={{ textTransform: 'none', fontSize: '12px', fontWeight: 600, color: confirmationSent.copied ? '#00A76F' : '#637381' }}>
                                                    {confirmationSent.copied ? 'Copied!' : 'Copy'}
                                                </Button>
                                            </Box>
                                        </Box>

                                        {/* Expired window banner */}
                                        {isWindowExpired && (
                                            <Box sx={{ px: 3, py: 1.25, background: 'rgba(255,171,0,0.08)', borderTop: '1px solid rgba(255,171,0,0.2)', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Icon icon="mdi:clock-alert-outline" width={16} style={{ color: '#B76E00', flexShrink: 0 }} />
                                                <Typography sx={{ fontSize: '12px', color: '#B76E00', lineHeight: 1.4 }}>
                                                    24-hour window expired. Copy the message and send via another channel.
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Footer */}
                                        <Box sx={{ px: 3, py: 2, borderTop: isWindowExpired ? 'none' : '1px solid rgba(145,158,171,0.12)', background: '#f9fafb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography sx={{ fontSize: '11px', color: '#C4CDD5' }}>
                                                {isWindowExpired ? 'Use template or copy' : '⌘+Enter to send'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button onClick={() => setConfirmPreview(null)} sx={{ textTransform: 'none', fontSize: '13px', color: '#637381' }}>Cancel</Button>
                                                {isWindowExpired ? (
                                                    <Button variant="contained"
                                                        startIcon={<Icon icon={confirmationSent.copied ? 'eva:checkmark-fill' : 'eva:copy-fill'} width={16} />}
                                                        onClick={handleCopyMessage}
                                                        sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600, borderRadius: 2, background: '#FFAB00', '&:hover': { background: '#B76E00' }, boxShadow: 'none' }}>
                                                        {confirmationSent.copied ? 'Copied!' : 'Copy Message'}
                                                    </Button>
                                                ) : (
                                                    <Button variant="contained"
                                                        startIcon={<Icon icon="eva:paper-plane-fill" width={16} />}
                                                        disabled={!editableMessage?.trim() || (editableMessage?.length || 0) > 4096}
                                                        onClick={() => {
                                                            if (onShareMessage) onShareMessage(editableMessage);
                                                            setConfirmationSent(prev => ({ ...prev, whatsapp: true }));
                                                            setConfirmPreview(null);
                                                        }}
                                                        sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600, borderRadius: 2, background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)', '&:hover': { background: 'linear-gradient(135deg, #1da851 0%, #0e7a6b 100%)' } }}>
                                                        Send to Chat
                                                    </Button>
                                                )}
                                            </Box>
                                        </Box>
                                    </Dialog>

                                    {/* Email confirmation panel */}
                                    {confirmPreview === 'email' && (
                                        <Box sx={{ background: '#eff6ff', borderRadius: RADIUS, border: '1px solid #bfdbfe', p: 1.25 }}>
                                            {/* Step 1: No email — enter email */}
                                            {!(leadDetails?.lead_contact?.email || leadDetails?.email) && !confirmationSent.emailSaved && (
                                                <Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                                                        <Icon icon="eva:alert-circle-fill" width={16} style={{ color: '#FFAB00' }} />
                                                        <Typography sx={{ fontSize: '12px', color: '#B76E00', fontWeight: 500 }}>No email address on file</Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <TextField size="small" fullWidth value={editEmail}
                                                            onChange={(e) => setEditEmail(e.target.value)}
                                                            placeholder="Enter email address" type="email" autoFocus
                                                            sx={COMPACT_INPUT_SX} />
                                                        <Button size="small" variant="contained" disabled={!editEmail?.includes('@')}
                                                            onClick={async () => {
                                                                if (leadId && practiceId) {
                                                                    await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`, 'patch', { lead_contact: { email: editEmail } });
                                                                    setConfirmationSent(prev => ({ ...prev, emailSaved: true }));
                                                                }
                                                            }}
                                                            sx={{ textTransform: 'none', fontSize: '11px', fontWeight: 600, borderRadius: RADIUS, background: '#078DEE', '&:hover': { background: '#0560b0' }, boxShadow: 'none', minWidth: 'auto', px: 1.5 }}>
                                                            Save & Preview
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            )}

                                            {/* Step 2: Email exists or just saved — show preview */}
                                            {((leadDetails?.lead_contact?.email || leadDetails?.email) || confirmationSent.emailSaved) && (
                                                <Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                                                        <Icon icon="eva:checkmark-circle-2-fill" width={16} style={{ color: '#078DEE' }} />
                                                        <Typography sx={{ fontSize: '12px', color: '#078DEE', fontWeight: 600 }}>
                                                            {isAlreadyBooked ? 'Confirmation email was sent' : 'Confirmation email sent'}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, pl: 0.25 }}>
                                                        <Icon icon="eva:email-outline" width={12} style={{ color: '#919EAB' }} />
                                                        <Typography sx={{ fontSize: '11px', color: '#637381' }}>{editEmail || leadDetails?.lead_contact?.email || leadDetails?.email}</Typography>
                                                    </Box>

                                                    {/* Compact preview + Edit & Send button */}
                                                    <Box sx={{ background: '#fff', borderRadius: RADIUS, border: '1px solid rgba(145,158,171,0.12)', p: 1, mb: 1, maxHeight: 80, overflow: 'auto' }}>
                                                        <Typography sx={{ fontSize: '10px', color: '#919EAB', fontWeight: 600, mb: 0.25 }}>{buildEmailSubject()}</Typography>
                                                        <Typography sx={{ fontSize: '11px', color: '#637381', whiteSpace: 'pre-line', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                                                            {personalEmailBody || buildEmailBody()}
                                                        </Typography>
                                                    </Box>
                                                    <Button size="small" variant="contained" fullWidth
                                                        onClick={() => { setConfirmPreview('emailDialog'); if (!personalEmailBody) setPersonalEmailBody(buildEmailBody()); }}
                                                        disabled={confirmationSent.email === true}
                                                        startIcon={<Icon icon={confirmationSent.email === true ? 'eva:checkmark-circle-2-fill' : 'eva:edit-2-fill'} width={14} />}
                                                        sx={{ textTransform: 'none', fontSize: '12px', fontWeight: 600, borderRadius: RADIUS, background: '#078DEE', '&:hover': { background: '#0560b0' }, boxShadow: 'none', py: 0.75, mb: 0.5 }}>
                                                        {/* eslint-disable-next-line no-nested-ternary */}
                                                        {confirmationSent.email === true ? 'Email Sent' : isAlreadyBooked ? 'Edit & Resend' : 'Edit & Send'}
                                                    </Button>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Typography sx={{ fontSize: '10px', color: '#919EAB' }}>
                                                            {isAlreadyBooked ? 'Original was sent via sequence.' : 'Will be sent via sequence.'}
                                                        </Typography>
                                                        <Tooltip title="Changes template for ALL patients" arrow placement="top">
                                                            <Button size="small" variant="text"
                                                                onClick={() => window.open('/dashboard/user/message', '_blank')}
                                                                startIcon={<Icon icon="eva:settings-2-outline" width={12} />}
                                                                sx={{ textTransform: 'none', fontSize: '10px', fontWeight: 600, color: '#FF5630', minWidth: 'auto', px: 0.75, py: 0.25, '&:hover': { background: 'rgba(255,86,48,0.06)' } }}>
                                                                Edit global
                                                            </Button>
                                                        </Tooltip>
                                                    </Box>

                                                    {/* Email edit dialog — matches WhatsApp dialog style */}
                                                    <Dialog
                                                        open={confirmPreview === 'emailDialog'}
                                                        onClose={() => setConfirmPreview('email')}
                                                        maxWidth="sm" fullWidth
                                                        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' } }}
                                                    >
                                                        <Box sx={{ background: 'linear-gradient(135deg, #078DEE 0%, #0560b0 100%)', px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    <Icon icon="eva:email-fill" width={20} style={{ color: '#fff' }} />
                                                                </Box>
                                                                <Box>
                                                                    <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '15px' }}>Email Confirmation</Typography>
                                                                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>{editEmail || leadDetails?.lead_contact?.email || leadDetails?.email}</Typography>
                                                                </Box>
                                                            </Box>
                                                            <IconButton onClick={() => setConfirmPreview('email')} sx={{ color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
                                                                <Icon icon="eva:close-fill" width={20} />
                                                            </IconButton>
                                                        </Box>
                                                        <Box sx={{ p: 3 }}>
                                                            <Typography sx={{ fontSize: '12px', color: '#637381', fontWeight: 600, mb: 1 }}>Subject: {buildEmailSubject()}</Typography>
                                                            <TextField multiline minRows={6} maxRows={12} fullWidth autoFocus
                                                                value={personalEmailBody} onChange={(e) => setPersonalEmailBody(e.target.value)}
                                                                placeholder="Edit email content..."
                                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '14px', lineHeight: 1.6, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#078DEE' } } }}
                                                            />
                                                            <Typography sx={{ fontSize: '9px', color: '#919EAB', mt: 0.75, fontStyle: 'italic' }}>
                                                                This only changes the email for this patient. Use &quot;Edit global&quot; to change the template for everyone.
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ px: 3, py: 2, borderTop: '1px solid rgba(145,158,171,0.12)', background: '#f9fafb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography sx={{ fontSize: '11px', color: '#C4CDD5' }}>⌘+Enter to send</Typography>
                                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                                <Button onClick={() => setConfirmPreview('email')} sx={{ textTransform: 'none', fontSize: '13px', color: '#637381' }}>Cancel</Button>
                                                                <Button variant="contained" onClick={handleSendEmail}
                                                                    disabled={emailSending || !personalEmailBody?.trim()}
                                                                    startIcon={emailSending ? <CircularProgress size={14} color="inherit" /> : <Icon icon="eva:paper-plane-fill" width={16} />}
                                                                    sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600, borderRadius: 2, background: 'linear-gradient(135deg, #078DEE 0%, #0560b0 100%)', boxShadow: '0 4px 12px rgba(7,141,238,0.3)', '&:hover': { background: 'linear-gradient(135deg, #0560b0 0%, #043d7a 100%)' } }}>
                                                                    {isAlreadyBooked ? 'Resend' : 'Send'}
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    </Dialog>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>

                                <Box sx={{ borderTop: SECTION_BORDER, my: 0.5 }} />

                                <Button
                                    variant="outlined" fullWidth
                                    onClick={() => setShowBookingForm(true)}
                                    startIcon={<Icon icon="mdi:calendar-plus" width={16} />}
                                    sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600, borderRadius: RADIUS, borderColor: '#00A76F', color: '#00A76F', '&:hover': { borderColor: '#007B55', background: 'rgba(0, 167, 111, 0.04)' } }}
                                >
                                    Book another appointment
                                </Button>
                                <Button
                                    variant="contained" fullWidth
                                    onClick={handleDone}
                                    disabled={isClosing}
                                    startIcon={isClosing ? <CircularProgress size={14} color="inherit" /> : <Icon icon="eva:checkmark-circle-2-fill" width={16} />}
                                    sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600, borderRadius: RADIUS, background: '#00A76F', '&:hover': { background: '#007B55' }, boxShadow: 'none', py: 1 }}
                                >
                                    {isClosing ? 'Closing...' : 'Mark as handled'}
                                </Button>
                                <Button
                                    variant="text" fullWidth
                                    startIcon={<Icon icon="eva:external-link-fill" width={16} />}
                                    onClick={() => window.open(`/dashboard/practice/${practiceId}/lead-detail/${leadId}`, '_blank')}
                                    sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 500, borderRadius: RADIUS, color: '#637381' }}
                                >
                                    View lead
                                </Button>
                            </Stack>
                        </Box>
                    ) : (
                        <Stack spacing={0}>
                            {/* Patient detail icons */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                                {[
                                    { key: 'personal', icon: 'eva:person-fill', tooltip: (editFirstName && editLastName) ? 'Personal details' : 'Add personal details', hasData: !!(editFirstName && editLastName) },
                                    { key: 'phone', icon: 'eva:phone-fill', tooltip: editPhone ? 'Phone' : 'Add phone', hasData: !!editPhone },
                                    { key: 'email', icon: 'eva:email-fill', tooltip: editEmail ? 'Email' : 'Add email', hasData: !!editEmail },
                                    { key: 'address', icon: 'eva:home-fill', tooltip: editAddress ? 'Address' : 'Add address', hasData: !!editAddress },
                                ].map((item) => (
                                    <Tooltip key={item.key} title={item.tooltip} arrow placement="top">
                                        <Box tabIndex={-1} onClick={() => { setShowDetails(prev => prev === item.key ? null : item.key); setShowTimeline(false); }}
                                            sx={{
                                                width: 30, height: 30, borderRadius: RADIUS,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                cursor: 'pointer', transition: 'all 150ms ease', position: 'relative',
                                                border: item.hasData ? '1px solid' : '1.5px dashed',
                                                // eslint-disable-next-line no-nested-ternary
                                                borderColor: showDetails === item.key ? '#00A76F' : item.hasData ? '#e0e0e0' : '#FFAB00',
                                                background: showDetails === item.key ? 'rgba(0, 167, 111, 0.06)' : 'transparent',
                                                // eslint-disable-next-line no-nested-ternary
                                                color: showDetails === item.key ? '#00A76F' : item.hasData ? '#637381' : '#C4CDD5',
                                                '&:hover': { borderColor: '#919EAB', background: '#fafafa' },
                                                '&:hover .pencil-badge': { opacity: 1 },
                                            }}>
                                            <Icon icon={item.icon} width={14} />
                                            {!item.hasData ? (
                                                <Box sx={{ position: 'absolute', top: -2, right: -2, width: 7, height: 7, borderRadius: '50%', background: '#FFAB00', border: '1.5px solid #fff' }} />
                                            ) : (
                                                <Box className="pencil-badge" sx={{ position: 'absolute', bottom: -2, right: -2, width: 12, height: 12, borderRadius: '50%', background: '#fff', border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 150ms' }}>
                                                    <Icon icon="eva:edit-2-fill" width={7} style={{ color: '#919EAB' }} />
                                                </Box>
                                            )}
                                        </Box>
                                    </Tooltip>
                                ))}

                                {/* Timeline icon — right side */}
                                <Tooltip title={showTimeline ? 'Back to booking' : 'Activity timeline'} arrow placement="top">
                                    <Box tabIndex={-1} onClick={() => { setShowTimeline(prev => !prev); setShowDetails(null); }}
                                        sx={{
                                            width: 30, height: 30, borderRadius: RADIUS, ml: 'auto',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            cursor: 'pointer', transition: 'all 150ms ease',
                                            border: '1px solid', borderColor: showTimeline ? '#078DEE' : '#e0e0e0',
                                            background: showTimeline ? 'rgba(7,141,238,0.06)' : 'transparent',
                                            color: showTimeline ? '#078DEE' : '#919EAB',
                                            '&:hover': { borderColor: '#078DEE', color: '#078DEE', background: 'rgba(7,141,238,0.04)' },
                                        }}>
                                        <Icon icon="eva:activity-fill" width={14} />
                                    </Box>
                                </Tooltip>
                            </Box>

                            {/* Overview/Timeline view — same as contact flow right panel */}
                            {showTimeline && leadId && (
                                <Box sx={{ mt: 1, mb: 1, maxHeight: 'calc(100vh - 300px)', overflow: 'auto', mx: -2.5, px: 0.5 }}>
                                    <OverviewTab
                                        treatmentTabData={leadDetails?.lead_treatments || fetchedTreatments || []}
                                        leadId={leadId}
                                        handleChange={() => {}}
                                        user={leadDetails}
                                        LeadNotes={[]}
                                    />
                                </Box>
                            )}

                            {/* Expanded detail inputs */}
                            {showDetails && (
                                <Box sx={{ mt: 1.5, mb: 1.5 }}>
                                    {showDetails === 'personal' && (
                                        <Stack spacing={1}>
                                            <Box sx={{ display: 'flex', gap: 0.75 }}>
                                                <Select value={editSalutation} onChange={(e) => {
                                                    const nextSalutation = e.target.value;
                                                    setEditSalutation(nextSalutation);
                                                    triggerAutoSave({ salutation: nextSalutation });
                                                }}
                                                    displayEmpty size="small" sx={{ width: 80, ...COMPACT_SELECT_SX }}>
                                                    <MenuItem value=""><Typography sx={{ color: '#919EAB', fontSize: '12px' }}>Title</Typography></MenuItem>
                                                    {availableSalutationOptions.map((option) => (
                                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                    ))}
                                                </Select>
                                                <TextField size="small" value={editFirstName} onChange={(e) => { setEditFirstName(e.target.value); triggerAutoSave(); }}
                                                    placeholder="First name" autoFocus sx={{ flex: 1, ...COMPACT_INPUT_SX }} />
                                                <TextField size="small" value={editLastName} onChange={(e) => { setEditLastName(e.target.value); triggerAutoSave(); }}
                                                    placeholder="Last name" sx={{ flex: 1, ...COMPACT_INPUT_SX }} />
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 0.75 }}>
                                                {/* Keyed by scope: a lead/practice switch discards any open picker draft
                                                    instead of saving it into the new lead. The field only emits on OK/Clear,
                                                    so autosave never fires for a half-made selection. Clear is local-only:
                                                    the autosave payload omits an empty DOB, so nothing is written. */}
                                                <DateOfBirthField key={dentallyUiScopeKey} size="small" label="" placeholder="DOB" value={editDob} onChange={(nextDob) => {
                                                    setEditDob(nextDob);
                                                    if (nextDob) triggerAutoSave({ dob: nextDob });
                                                }}
                                                    sx={{ flex: 1, ...COMPACT_INPUT_SX, '& .MuiInputBase-input': { py: '6px', fontSize: '12px', color: editDob ? '#212B36' : '#919EAB' } }} />
                                            </Box>
                                        </Stack>
                                    )}
                                    {showDetails === 'phone' && (
                                        <TextField size="small" fullWidth value={editPhone} onChange={(e) => { setEditPhone(e.target.value); triggerAutoSave(); }}
                                            placeholder="Enter phone number" autoFocus sx={COMPACT_INPUT_SX} />
                                    )}
                                    {showDetails === 'email' && (
                                        <TextField size="small" fullWidth value={editEmail} onChange={(e) => { setEditEmail(e.target.value); triggerAutoSave(); }}
                                            placeholder="Enter email address" type="email" autoFocus sx={COMPACT_INPUT_SX} />
                                    )}
                                    {showDetails === 'address' && (
                                        <GoogleAutocomplete
                                            onPlaceSelected={(place, inputRef) => { setEditAddress(inputRef.value); triggerAutoSave(); }}
                                            onChange={(e) => setEditAddress(e.target.value)}
                                            onBlur={() => { if (editAddress) triggerAutoSave(); }}
                                            options={{ types: ['establishment', 'geocode'], fields: ['address_components', 'formatted_address'], componentRestrictions: { country: 'uk' } }}
                                            defaultValue={editAddress} placeholder="Search postcode or address"
                                            style={{ width: '100%', padding: '6px 12px', fontSize: '12px', height: '32px', boxSizing: 'border-box', borderRadius: RADIUS, border: '1px solid rgba(145,158,171,0.16)', outline: 'none', fontFamily: 'inherit' }}
                                        />
                                    )}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 0.75, height: 20 }}>
                                        {detailSaveStatus === 'saving' && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <CircularProgress size={10} sx={{ color: '#919EAB' }} />
                                                <Typography sx={{ fontSize: '11px', color: '#919EAB' }}>Saving...</Typography>
                                            </Box>
                                        )}
                                        {detailSaveStatus === 'saved' && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Icon icon="eva:checkmark-circle-2-fill" width={14} style={{ color: '#00A76F' }} />
                                                <Typography sx={{ fontSize: '11px', color: '#00A76F', fontWeight: 600 }}>Saved</Typography>
                                            </Box>
                                        )}
                                        {detailSaveStatus === 'error' && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Icon icon="eva:alert-circle-fill" width={14} style={{ color: '#FF5630' }} />
                                                <Typography sx={{ fontSize: '11px', color: '#FF5630', fontWeight: 600 }}>Failed to save</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {/* Treatment + Reason (Dentally) — hidden when editing patient details */}
                            {(!showDetails || showDetails === 'notes') && !showTimeline && (<>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                                <Tooltip title="Edit treatment" arrow placement="right">
                                    <Box onClick={() => setEditingTreatment(prev => !prev)}
                                        sx={{
                                            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            py: 0.625, px: 1.25, cursor: 'pointer', borderRadius: RADIUS, background: '#f8f9fa',
                                            transition: 'all 150ms ease',
                                            '&:hover': { background: '#f0f2f5' }, '&:hover .edit-icon': { opacity: 1 },
                                        }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                            <Icon icon="mdi:tooth-outline" width={14} style={{ color: '#919EAB', flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: '12px', color: '#212B36', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{treatmentName}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                            {needsTeethCount && (
                                                <Box component="input" type="number" value={teethCount} onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) => setTeethCount(e.target.value)}
                                                    onBlur={() => { if (teethCount) saveTreatmentChange(null, teethCount); }}
                                                    placeholder="#" min={1} max={32}
                                                    sx={{ width: 28, height: 24, textAlign: 'center', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '11px', fontWeight: 600, color: '#212B36', outline: 'none', background: '#fff', '&:focus': { borderColor: '#00A76F' }, MozAppearance: 'textfield', '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 } }} />
                                            )}
                                            {treatmentPrice && treatmentPrice !== '—' && (
                                                <Typography sx={{ fontSize: '12px', color: '#00A76F', fontWeight: 600 }}>&pound;{treatmentPrice}</Typography>
                                            )}
                                            <Icon icon="eva:edit-2-outline" width={12} className="edit-icon" style={{ color: '#919EAB', opacity: 0, transition: 'opacity 150ms' }} />
                                        </Box>
                                    </Box>
                                </Tooltip>
                                {useDentally && (
                                    <Select value={dentallyReason} onChange={(e) => setDentallyReason(e.target.value)}
                                        size="small" displayEmpty sx={{ ...COMPACT_SELECT_SX, width: 110, flexShrink: 0 }}>
                                        {DENTALLY_REASON_OPTIONS.map((r) => <MenuItem key={r} value={r} sx={{ fontSize: '11px' }}>{r}</MenuItem>)}
                                    </Select>
                                )}
                            </Box>
                            {editingTreatment && (
                                <Box sx={{ mb: 1 }}>
                                    <Select value={activeTreatmentId || ''} onChange={(e) => { const val = e.target.value || null; setSelectedTreatmentId(val); setEditingTreatment(false); if (val) saveTreatmentChange(val); }}
                                        size="small" fullWidth displayEmpty sx={COMPACT_SELECT_SX}>
                                        <MenuItem value=""><Typography variant="body2" sx={{ color: '#919EAB', fontSize: '12px' }}>Select treatment</Typography></MenuItem>
                                        {(practiceTreatments.length > 0 ? practiceTreatments : (leadDetails?.lead_treatments || fetchedTreatments || [])).map((t) => (
                                            <MenuItem key={t.id} value={t.id}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                                                    <Typography sx={{ fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.treatment_item?.name || t.treatment?.name || (typeof t.treatment === 'string' ? t.treatment : null) || t.treatment_name || t.name || '—'}</Typography>
                                                    {t.price && <Typography sx={{ fontSize: '12px', color: '#919EAB', flexShrink: 0 }}>&pound;{t.price}</Typography>}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            )}
                            </>)}

                            {/* Form — collapses when editing patient details */}
                            {(!showDetails || showDetails === 'notes') && !showTimeline && (
                            <>
                            <Box sx={{ borderTop: SECTION_BORDER, mt: 1, mb: 2 }} />

                            {/* Booked with + Assigned to — CRM only (Dentally has these in DentallyAvailabilityStep) */}
                            {!useDentally && (
                            <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                                {bookableStaff.length > 0 && (
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={LABEL_SX}>Booked with <span style={{ color: '#FF5630' }}>*</span></Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {bookableStaff.map((u) => {
                                                const rc = getRoleColor(u.role, bookedWith === u.id);
                                                const isPractitioner = u.role?.toUpperCase() === 'PRACTITIONER';
                                                return (
                                                    <Tooltip key={u.id} title={u.name} arrow placement="top"
                                                        componentsProps={{ tooltip: { sx: { bgcolor: isPractitioner ? '#078DEE' : '#855CF8', fontSize: '11px', fontWeight: 600 } }, arrow: { sx: { color: isPractitioner ? '#078DEE' : '#855CF8' } } }}>
                                                        <Box onClick={() => setBookedWith(u.id)}
                                                            sx={{ width: 30, height: 30, borderRadius: '50%', overflow: 'hidden', cursor: 'pointer', border: '2px solid', borderColor: rc.border, display: 'flex', alignItems: 'center', justifyContent: 'center', background: rc.bg, fontSize: '11px', fontWeight: 600, color: rc.text, transition: 'all 150ms ease', '&:hover': { borderColor: ROLE_AVATAR_COLORS[u.role?.toUpperCase()]?.border || '#C4CDD5' } }}>
                                                            {u.avatar ? <img src={u.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : `${(u.name || '?')[0]}`}
                                                        </Box>
                                                    </Tooltip>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                )}
                                {assignableUsers.length > 0 && (
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={LABEL_SX}>Assigned to</Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {assignableUsers.map((u) => {
                                                const rc = getRoleColor(u.role, appointmentAssignee === u.id);
                                                return (
                                                    <Tooltip key={u.id} title={u.name} arrow placement="top"
                                                        componentsProps={{ tooltip: { sx: { bgcolor: '#855CF8', fontSize: '11px', fontWeight: 600 } }, arrow: { sx: { color: '#855CF8' } } }}>
                                                        <Box onClick={() => setAppointmentAssignee(u.id)}
                                                            sx={{ width: 30, height: 30, borderRadius: '50%', overflow: 'hidden', cursor: 'pointer', border: '2px solid', borderColor: rc.border, display: 'flex', alignItems: 'center', justifyContent: 'center', background: rc.bg, fontSize: '11px', fontWeight: 600, color: rc.text, transition: 'all 150ms ease', '&:hover': { borderColor: ROLE_AVATAR_COLORS['TCO/USER'].border } }}>
                                                            {u.avatar ? <img src={u.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : `${(u.name || '?')[0]}`}
                                                        </Box>
                                                    </Tooltip>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                            )}

                            {/* ═══ DENTALLY MODE ═══ */}
                            {useDentally ? (
                                <>

                                    {/* Dentally Availability Step — compacted for 360px side panel */}
                                    <Suspense fallback={<Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>}>
                                        <Box sx={{
                                            // Compact toggle buttons — match CRM quick preset chips
                                            '& .MuiToggleButton-root': {
                                                height: 'auto !important', fontSize: '10px !important',
                                                p: '3px 8px !important', m: '0 3px 3px 0 !important',
                                                borderRadius: '6px !important', border: 'none !important',
                                                background: '#f4f6f8 !important', color: '#919EAB !important',
                                                '&.Mui-selected': { background: 'rgba(0, 167, 111, 0.06) !important', color: '#00A76F !important', border: 'none !important' },
                                            },
                                            '& .MuiToggleButton-root .iconify, & .MuiToggleButton-root .MuiSvgIcon-root': { display: 'none !important' },
                                            '& .MuiToggleButtonGroup-root': { flexWrap: 'wrap', gap: 0, border: 'none !important' },

                                            // Compact inputs
                                            '& .MuiTextField-root .MuiOutlinedInput-root': { fontSize: '12px !important', borderRadius: `${RADIUS} !important`, height: '32px !important' },
                                            '& .MuiInputBase-input': { py: '6px !important', fontSize: '12px !important' },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16) !important' },
                                            '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00A76F !important', borderWidth: '1px !important' },
                                            '& .MuiSelect-select': { fontSize: '11px !important', py: '6px !important' },

                                            // Compact typography
                                            '& .MuiTypography-subtitle2': { fontSize: '11px !important', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#919EAB !important', fontWeight: '600 !important', mb: '4px !important' },
                                            '& .MuiTypography-caption': { fontSize: '10px !important' },
                                            '& .MuiTypography-body2': { fontSize: '11px !important' },
                                            '& .MuiChip-root': { height: '18px !important', fontSize: '9px !important', minWidth: '16px !important' },
                                            '& .MuiButton-root:not(.MuiToggleButton-root)': { fontSize: '11px !important', py: '4px !important', borderRadius: `${RADIUS} !important` },
                                            '& .MuiCheckbox-root': { p: '2px !important' },
                                            '& .MuiStack-root': { gap: '6px !important' },
                                            '& .MuiAvatar-root': { width: '16px !important', height: '16px !important', fontSize: '7px !important' },
                                        }}>
                                            <DentallyAvailabilityStep
                                                compact
                                                practiceId={practiceId}
                                                selectedSlot={dentallySlot}
                                                onSelect={updateDentallySlotForScope}
                                                assignToState={dentallyAssignees}
                                                onAssignToChange={updateDentallyAssigneesForScope}
                                                availSearchState={dentallyAvailSearch}
                                                onAvailSearchStateChange={updateDentallySearchForScope}
                                            />
                                        </Box>
                                    </Suspense>

                                    {/* Selected slot summary */}
                                    {dentallySlot && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, px: 1.25, borderRadius: RADIUS, mt: 1.5, background: 'rgba(0, 167, 111, 0.04)', border: '1px solid rgba(0, 167, 111, 0.16)' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Icon icon="eva:calendar-fill" width={14} style={{ color: '#00A76F' }} />
                                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#212B36' }}>
                                                    {moment(parseDentallyDate(dentallySlot.start_time)).format('ddd D MMM YYYY, HH:mm')}
                                                </Typography>
                                            </Box>
                                            <Typography onClick={() => setDentallySlot(null)} sx={{ fontSize: '11px', color: '#919EAB', cursor: 'pointer', fontWeight: 600, '&:hover': { color: '#FF5630' } }}>Change</Typography>
                                        </Box>
                                    )}

                                    {dentallyBooking.error && (
                                        <Typography sx={{ fontSize: '12px', color: '#FF5630', mt: 1 }}>{dentallyBooking.error.message || 'Booking failed'}</Typography>
                                    )}

                                    <Typography sx={{ fontSize: '10px', color: '#C4CDD5', mt: 1, fontStyle: 'italic' }}>
                                        Need to record a past appointment? Switch off Dentally to log it CRM-only.
                                    </Typography>

                                    <Box sx={{ borderTop: SECTION_BORDER, my: 1.5 }} />
                                </>
                            ) : (
                                <>
                            {/* ═══ CRM MODE ═══ */}

                            {/* Appointment type chips */}
                            <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
                                {(appointmentTypes?.length > 0 ? appointmentTypes : [
                                    { label: 'Consultation', value: 'CONSULTATION' },
                                    { label: 'Review', value: 'REVIEW' },
                                ]).map((t) => (
                                    <Box key={t.value} onClick={() => setAppointmentType(t.value)}
                                        sx={{
                                            px: 1.25, py: 0.375, borderRadius: '14px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                                            border: '1px solid', borderColor: appointmentType === t.value ? '#00A76F' : '#e0e0e0',
                                            color: appointmentType === t.value ? '#00A76F' : '#637381',
                                            background: appointmentType === t.value ? 'rgba(0, 167, 111, 0.06)' : 'transparent',
                                            transition: 'all 150ms ease', '&:hover': { borderColor: '#919EAB', background: '#fafafa' },
                                        }}>
                                        {t.label}
                                    </Box>
                                ))}
                                <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 0.5 }}>
                                    <Typography sx={{ fontSize: '11px', color: '#C4CDD5' }}>
                                        {(appointmentMethods?.find(m => m.value === appointmentMethod)?.label) || 'In Practice'}
                                    </Typography>
                                    <Typography onClick={() => setShowMethodPicker(prev => !prev)}
                                        sx={{ fontSize: '11px', color: '#00A76F', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
                                        Edit
                                    </Typography>
                                </Box>
                            </Box>

                            {showMethodPicker && (
                                <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5 }}>
                                    {(appointmentMethods?.length > 0 ? appointmentMethods : [
                                        { label: 'In Practice', value: 'IN_PRACTICE' },
                                        { label: 'Video Call', value: 'VIDEO_CALL' },
                                        { label: 'Phone', value: 'PHONE' },
                                    ]).map((m) => (
                                        <Box key={m.value} onClick={() => { setAppointmentMethod(m.value); setShowMethodPicker(false); }}
                                            sx={{
                                                flex: 1, textAlign: 'center', py: 0.375, borderRadius: RADIUS, fontSize: '11px',
                                                fontWeight: m.value === appointmentMethod ? 600 : 500, cursor: 'pointer', border: '1px solid',
                                                borderColor: m.value === appointmentMethod ? '#00A76F' : '#e0e0e0',
                                                color: m.value === appointmentMethod ? '#00A76F' : '#637381',
                                                background: m.value === appointmentMethod ? 'rgba(0, 167, 111, 0.06)' : 'transparent',
                                                transition: 'all 150ms ease', '&:hover': { borderColor: '#919EAB', background: '#fafafa' },
                                            }}>
                                            {m.label}
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            {/* Date & Time */}
                            {!appointmentDate ? (
                                <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                        <Typography sx={{ ...LABEL_SX, mb: 0, mr: 0.5 }}>When</Typography>
                                        {QUICK_PRESETS.map((preset) => (
                                            <Box key={preset.label} onClick={() => setAppointmentDate(preset.getValue())}
                                                sx={{
                                                    px: 0.75, py: 0.25, borderRadius: '6px', fontSize: '10px', fontWeight: 600, cursor: 'pointer',
                                                    color: '#919EAB', background: '#f4f6f8', whiteSpace: 'nowrap',
                                                    '&:hover': { color: '#00A76F', background: 'rgba(0, 167, 111, 0.06)' }, transition: 'all 150ms ease',
                                                }}>
                                                {preset.label}
                                            </Box>
                                        ))}
                                    </Box>
                                    {/* CRM mode allows past dates — the backend auto-marks a past-dated
                                        appointment ATTENDED on creation (see handleBookCRM). Dentally
                                        mode keeps its own future-only availability picker. */}
                                    <DateTimePicker value={appointmentDate} onChange={(v) => setAppointmentDate(v)}
                                        format="dd/MM/yyyy HH:mm" ampm={false}
                                        slotProps={{ textField: { fullWidth: true, size: 'small', placeholder: 'Pick a date & time', sx: COMPACT_INPUT_SX } }} />
                                </Box>
                            ) : (
                                <Box sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, px: 1.25, borderRadius: RADIUS, background: 'rgba(0, 167, 111, 0.04)', border: '1px solid rgba(0, 167, 111, 0.16)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Icon icon="eva:calendar-fill" width={14} style={{ color: '#00A76F' }} />
                                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#212B36' }}>{moment(appointmentDate).format('ddd D MMM YYYY, HH:mm')}</Typography>
                                        </Box>
                                        <Typography onClick={() => setAppointmentDate(null)} sx={{ fontSize: '11px', color: '#919EAB', cursor: 'pointer', fontWeight: 600, '&:hover': { color: '#FF5630' } }}>Change</Typography>
                                    </Box>
                                    {new Date(appointmentDate).getTime() < Date.now() && (
                                        <Typography sx={{ fontSize: '11px', color: '#B76E00', mt: 0.5 }}>
                                            Past date — this will be recorded as attended.
                                        </Typography>
                                    )}
                                </Box>
                            )}

                                <Box sx={{ borderTop: SECTION_BORDER, mb: 1.5 }} />
                                </>
                            )}


                            {/* Notes — compact icon, expands on click */}
                            {/* eslint-disable-next-line no-nested-ternary */}
                            {showDetails === 'notes' ? (
                                /* Editing notes — open TextField */
                                <Box sx={{ mb: 1.5 }}>
                                    <TextField multiline minRows={3} maxRows={6} fullWidth size="small" value={notes}
                                        onChange={(e) => setNotes(e.target.value)} autoFocus
                                        onBlur={() => {
                                            setShowDetails(null);
                                            // Save note only if content changed since last save
                                            const trimmed = notes?.trim();
                                            if (trimmed && trimmed !== lastSavedNoteRef.current && leadId && practiceId) {
                                                lastSavedNoteRef.current = trimmed;
                                                callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/notes/`, 'post', {
                                                    note: 'FOLLOW_UP', additional_notes: trimmed, lead: leadId,
                                                });
                                            }
                                        }}
                                        placeholder="Additional notes..."
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: RADIUS, fontSize: '12px', lineHeight: 1.6,
                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16)' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.3)' },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00A76F', borderWidth: '1px' },
                                            },
                                            '& .MuiInputBase-input': { py: 1.25, px: 1.5 },
                                        }} />
                                </Box>
                            ) : notes?.trim() ? (
                                /* Has notes — show collapsed preview */
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, cursor: 'pointer', py: 0.5, '&:hover .note-text': { color: '#212B36' } }}
                                    onClick={() => setShowDetails('notes')}>
                                    <Icon icon="eva:file-text-fill" width={14} style={{ color: '#919EAB', flexShrink: 0 }} />
                                    <Typography className="note-text" sx={{ fontSize: '12px', color: '#637381', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, transition: 'color 150ms' }}>
                                        {notes.trim()}
                                    </Typography>
                                    <Icon icon="eva:edit-2-outline" width={12} style={{ color: '#C4CDD5', flexShrink: 0 }} />
                                </Box>
                            ) : (
                                /* No notes — show add prompt */
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, cursor: 'pointer', py: 0.5 }}
                                    onClick={() => setShowDetails('notes')}>
                                    <Icon icon="eva:edit-2-outline" width={14} style={{ color: '#C4CDD5' }} />
                                    <Typography sx={{ fontSize: '12px', color: '#C4CDD5', '&:hover': { color: '#919EAB' } }}>Add notes</Typography>
                                </Box>
                            )}

                            <Box sx={{ borderTop: SECTION_BORDER, mb: 1.5 }} />

                            {/* Send treatment info */}
                            <Button variant="text" fullWidth
                                onClick={() => { setEditableMessage(buildTreatmentInfoMessage()); setTreatmentInfoOpen(true); }}
                                startIcon={<Icon icon="ri:whatsapp-fill" width={15} />}
                                sx={{ textTransform: 'none', fontSize: '12px', fontWeight: 600, borderRadius: RADIUS, color: '#25D366', justifyContent: 'flex-start', px: 1, py: 0.75, '&:hover': { background: 'rgba(37,211,102,0.06)' } }}>
                                Send treatment info via WhatsApp
                            </Button>

                            {/* Treatment info edit dialog — same pattern as booking confirmation */}
                            <Dialog
                                open={treatmentInfoOpen}
                                onClose={() => setTreatmentInfoOpen(false)}
                                maxWidth="sm" fullWidth
                                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' } }}
                            >
                                <Box sx={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Icon icon="mdi:tooth-outline" width={20} style={{ color: '#fff' }} />
                                        </Box>
                                        <Box>
                                            <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '15px' }}>Treatment Information</Typography>
                                            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>{treatmentName}</Typography>
                                        </Box>
                                    </Box>
                                    <IconButton onClick={() => setTreatmentInfoOpen(false)} sx={{ color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
                                        <Icon icon="eva:close-fill" width={20} />
                                    </IconButton>
                                </Box>
                                <Box sx={{ p: 3 }}>
                                    <TextField
                                        multiline minRows={5} maxRows={12} fullWidth autoFocus
                                        value={editableMessage}
                                        onChange={(e) => setEditableMessage(e.target.value)}
                                        placeholder="Edit treatment information..."
                                        onKeyDown={(e) => {
                                            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !isWindowExpired) {
                                                if (onShareMessage) onShareMessage(editableMessage);
                                                setTreatmentInfoOpen(false);
                                            }
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '14px', lineHeight: 1.6, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#25D366' } } }}
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
                                        <Typography sx={{ fontSize: '11px', color: (editableMessage?.length || 0) > 4096 ? '#FF5630' : '#919EAB' }}>
                                            {editableMessage?.length || 0} / 4096
                                        </Typography>
                                        <Button size="small" variant="text"
                                            startIcon={<Icon icon={confirmationSent.copied ? 'eva:checkmark-fill' : 'eva:copy-fill'} width={14} />}
                                            onClick={handleCopyMessage}
                                            sx={{ textTransform: 'none', fontSize: '12px', fontWeight: 600, color: confirmationSent.copied ? '#00A76F' : '#637381' }}>
                                            {confirmationSent.copied ? 'Copied!' : 'Copy'}
                                        </Button>
                                    </Box>
                                </Box>
                                {isWindowExpired && (
                                    <Box sx={{ px: 3, py: 1.25, background: 'rgba(255,171,0,0.08)', borderTop: '1px solid rgba(255,171,0,0.2)', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Icon icon="mdi:clock-alert-outline" width={16} style={{ color: '#B76E00', flexShrink: 0 }} />
                                        <Typography sx={{ fontSize: '12px', color: '#B76E00', lineHeight: 1.4 }}>24-hour window expired. Copy the message and send via another channel.</Typography>
                                    </Box>
                                )}
                                <Box sx={{ px: 3, py: 2, borderTop: isWindowExpired ? 'none' : '1px solid rgba(145,158,171,0.12)', background: '#f9fafb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography sx={{ fontSize: '11px', color: '#C4CDD5' }}>{isWindowExpired ? 'Use template or copy' : '⌘+Enter to send'}</Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button onClick={() => setTreatmentInfoOpen(false)} sx={{ textTransform: 'none', fontSize: '13px', color: '#637381' }}>Cancel</Button>
                                        {isWindowExpired ? (
                                            <Button variant="contained"
                                                startIcon={<Icon icon={confirmationSent.copied ? 'eva:checkmark-fill' : 'eva:copy-fill'} width={16} />}
                                                onClick={handleCopyMessage}
                                                sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600, borderRadius: 2, background: '#FFAB00', '&:hover': { background: '#B76E00' }, boxShadow: 'none' }}>
                                                {confirmationSent.copied ? 'Copied!' : 'Copy Message'}
                                            </Button>
                                        ) : (
                                            <Button variant="contained"
                                                startIcon={<Icon icon="eva:paper-plane-fill" width={16} />}
                                                disabled={!editableMessage?.trim() || (editableMessage?.length || 0) > 4096}
                                                onClick={() => {
                                                    if (onShareMessage) onShareMessage(editableMessage);
                                                    setTreatmentInfoOpen(false);
                                                }}
                                                sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600, borderRadius: 2, background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)', '&:hover': { background: 'linear-gradient(135deg, #1da851 0%, #0e7a6b 100%)' } }}>
                                                Send to Chat
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </Dialog>
                            </>
                            )}
                        </Stack>
                    )}
                </Box>

                {/* Footer with progress */}
                {!bookingSuccess && (
                    <Box sx={{ borderTop: '1px solid rgba(145,158,171,0.12)', flexShrink: 0 }}>
                        <Box sx={{ height: 3, background: '#f4f6f8', overflow: 'hidden' }}>
                            <Box sx={{ height: '100%', width: `${(completedSteps / totalSteps) * 100}%`, background: allStepsComplete ? '#00A76F' : '#FFAB00', transition: 'width 400ms ease, background 400ms ease', borderRadius: '0 2px 2px 0' }} />
                        </Box>
                        <Box sx={{ px: 2.5, py: 1.25 }}>
                            <Button variant="contained" fullWidth onClick={handleBook} disabled={isBookDisabled}
                                sx={{
                                    background: allStepsComplete ? '#00A76F' : '#919EAB', '&:hover': { background: allStepsComplete ? '#007B55' : '#637381' },
                                    '&.Mui-disabled': { background: '#f4f6f8', color: '#C4CDD5' },
                                    textTransform: 'none', fontWeight: 600, fontSize: '13px', borderRadius: RADIUS, py: 1, boxShadow: 'none',
                                }}
                                startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <Icon icon="mdi:calendar-check" width={16} />}>
                                {/* eslint-disable-next-line no-nested-ternary */}
                                {isLoading ? 'Booking...' : allStepsComplete ? `Book ${useDentally ? 'via Dentally' : 'Appointment'}` : `Next: ${remainingSteps[0]?.label}`}
                            </Button>
                            {!allStepsComplete && !isSaving && remainingSteps.length > 1 && (
                                <Typography sx={{ fontSize: '10px', color: '#919EAB', mt: 0.5, textAlign: 'center' }}>
                                    Then: {remainingSteps.slice(1).map(s => s.label).join(' · ')}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

BookingPanel.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    contactDetails: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    taskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onShareMessage: PropTypes.func,
    embedded: PropTypes.bool,
    /** When provided, BookingPanel calls this instead of dispatching the thread-close itself.
     *  ResolvePanel uses this to own the ordering: checklist complete → thread close
     *  via runOutcomeSteps, so a checklist failure leaves the thread open. */
    onBooked: PropTypes.func,
};
